// The rep's route for the shown day (rep detail, WEB_PLAN.md §5.4): a read-only port of RouteDetailView's hero
// numbers, stop list and trip details. Per route: name, status ("Planned" / "In route" / "Completed"), when it was
// started and finished, stops done and skipped, planned vs GPS-driven distance, the time on the road, and the stops in
// visiting order with the iOS status line ("Done 2:43 PM", "Arrived 1:10 PM", "Skipped", "Planned 3:00 PM") and the
// NEXT stop. Stop numbers and colors match the numbered pins the map draws in its Route view.

import { CircleCheck, CircleSlash, Clock, MapPin, Route as RouteIcon, SkipForward } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { formatDistance, type DistanceUnit } from '@/contracts/enums';
import { Chip, EmptyState, ProgressBar, SectionCard, cn, readableTextOn } from '@/ui';
import {
  routeDurationText,
  routeProgressText,
  routeTimesText,
  type RouteCardModel,
  type RouteStopRow,
  type StopState,
} from './repRouteModel';

export interface RepRouteCardProps {
  routes: readonly RouteCardModel[];
  isToday: boolean;
  distanceUnit: DistanceUnit;
  className?: string;
}

export function RepRouteCard({ routes, isToday, distanceUnit, className }: RepRouteCardProps) {
  return (
    <SectionCard
      title={routes.length > 1 ? `Routes (${routes.length})` : 'Route'}
      padding={routes.length === 0 ? 'none' : 'md'}
      className={className}
    >
      {routes.length === 0 ? (
        <EmptyState
          compact
          icon={<RouteIcon />}
          title={isToday ? 'No route today' : 'No route on this day'}
          message={
            isToday
              ? 'When the rep plans or starts a route in the Ground Team app, its stops show up here as they are visited.'
              : 'The rep had no route planned or driven on this day.'
          }
        />
      ) : (
        <div className="flex flex-col divide-y divide-line">
          {routes.map((route, i) => (
            <RouteBlock key={route.id} route={route} number={routes.length > 1 ? i + 1 : undefined} unit={distanceUnit} />
          ))}
        </div>
      )}
    </SectionCard>
  );
}

/** `number`: with several routes on the day, "Route 2", as the map's switch calls it (agenda routes share a name). */
function RouteBlock({ route, number, unit }: { route: RouteCardModel; number: number | undefined; unit: DistanceUnit }) {
  const times = routeTimesText(route);
  const total = route.stops.length;
  return (
    <section
      aria-label={number !== undefined ? `Route ${number}: ${route.name}` : `Route ${route.name}`}
      className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="min-w-0 truncate text-sm font-semibold text-fg">
          {number !== undefined && <span className="text-fg-muted">Route {number} · </span>}
          {route.name}
        </h3>
        <Chip color={route.statusColor} filled={route.status === 'active'}>
          {route.statusTitle}
        </Chip>
        {route.isOptimized && <Chip color="#102A5E">Optimized</Chip>}
        <span className="flex-1" />
        <span className="text-xs text-fg-muted">{times ?? (route.status === 'planned' ? 'Not started yet' : '')}</span>
      </div>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
        <Stat
          label="Done"
          value={`${route.done} of ${total}`}
          sub={route.skipped > 0 ? `${route.skipped} skipped` : total === 1 ? '1 stop' : `${total} stops`}
        />
        <Stat
          label="Planned"
          value={route.plannedMeters > 0 ? formatDistance(unit, route.plannedMeters) : '—'}
          sub={route.estimatedSeconds > 0 ? `Est. ${routeDurationText(route.estimatedSeconds)}` : undefined}
        />
        <Stat
          label="Driven"
          value={route.drivenMeters !== null ? formatDistance(unit, route.drivenMeters) : '—'}
          sub={route.drivenMeters !== null ? 'GPS trail' : route.startedAt ? 'No GPS trail' : 'Not started'}
        />
        <Stat
          label={route.status === 'active' ? 'So far' : 'Time'}
          value={route.durationSeconds !== null ? routeDurationText(route.durationSeconds) : '—'}
          sub={route.durationSeconds !== null ? 'on the road' : undefined}
        />
      </dl>

      {total > 0 && <ProgressBar value={route.progress} tone="success" label={`${route.name}: ${routeProgressText(route)}`} />}

      {total === 0 ? (
        <p className="text-xs text-fg-subtle">No stops on this route yet.</p>
      ) : (
        <ol aria-label={`Stops of ${route.name}, in visiting order`} className="flex flex-col divide-y divide-line">
          {route.stops.map((stop) => (
            <StopRow key={stop.id} stop={stop} />
          ))}
        </ol>
      )}
    </section>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string | undefined }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] font-semibold tracking-[0.06em] text-fg-muted uppercase">{label}</dt>
      <dd className="font-rounded text-lg leading-tight font-bold text-fg tabular-nums">{value}</dd>
      {sub && <dd className="truncate text-[11px] text-fg-muted">{sub}</dd>}
    </div>
  );
}

/** DetailStopStatus's icon and tone per state. */
const STATUS_LOOK: Readonly<Record<StopState, { icon: ReactNode; className: string }>> = {
  done: { icon: <CircleCheck />, className: 'text-success-text' },
  skipped: { icon: <SkipForward />, className: 'text-fg-muted' },
  arrived: { icon: <MapPin />, className: 'text-link' },
  planned: { icon: <Clock />, className: 'text-fg-muted' },
  pending: { icon: <Clock />, className: 'text-fg-muted' },
  missed: { icon: <CircleSlash />, className: 'text-warning-text' },
};

function StopRow({ stop }: { stop: RouteStopRow }) {
  const look = STATUS_LOOK[stop.state];
  const name = stop.storeName ?? 'Store not available';
  return (
    <li className="flex items-start gap-3 py-2.5">
      <span
        aria-hidden
        className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ring-2 ring-surface"
        style={{ backgroundColor: stop.color, color: readableTextOn(stop.color) }}
      >
        {stop.label}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="sr-only">Stop {stop.number}:</span>
          {stop.storeName ? (
            <Link to={`/stores/${stop.recordId}`} className="truncate text-sm font-semibold text-fg hover:underline">
              {name}
            </Link>
          ) : (
            <span className="truncate text-sm text-fg-subtle">{name}</span>
          )}
          {stop.isNext && (
            <Chip size="xs" color="#F96A3C">
              Next
            </Chip>
          )}
        </span>
        <span className={cn('inline-flex items-center gap-1 text-[11px] [&>svg]:size-3', look.className)}>
          {look.icon}
          {stop.statusText}
        </span>
        {stop.onSite && <span className="text-[11px] text-fg-muted">{stop.onSite}</span>}
        {stop.notes && <span className="line-clamp-2 text-xs text-fg-subtle">{stop.notes}</span>}
      </div>
    </li>
  );
}
